<template>
  <v-container>
    <Slideout></Slideout>
    <v-row>
      <v-col class="col-12 col-sm-4">
        <PlaylistPreview></PlaylistPreview>
      </v-col>
      <v-col :class="{ marginLeft : $vuetify.breakpoint.sm }" class="mr-1">
        <v-row>
          <v-col>
            <v-text-field
            class="playlist-headline"
            v-model.trim="playlistName"
            counter
            :color="playlistColor"
            :error-messages="playlistNameErrors"
            append-icon="mdi-pencil"
            @input="$v.playlistName.$touch()"
            @blur="updatePlaylistName"
            @keyup.enter="updatePlaylistName">{{ playlist.name }}</v-text-field>
            <div :style="{ height: '5px' }">
              <v-progress-linear
                :active="isUpdating"
                indeterminate
              ></v-progress-linear>
            </div>
          </v-col>
        </v-row>
        <PlaylistSettings :key=settingsKey></PlaylistSettings>
        <MicroCredentialSettings v-if="hasMicroCredentialSetup"></MicroCredentialSettings>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import {
  mapState,
  mapGetters,
  mapMutations,
  mapActions,
} from 'vuex';
// Validations from Vuelidate
import {
  maxLength,
  required,
} from 'vuelidate/lib/validators';
import _ from 'lodash';

import { mapFields } from 'vuex-map-fields';
import MicroCredentialSettings from './MicroCredentialSettings.vue';
import PlaylistPreview from './PlaylistPreview.vue';
import PlaylistSettings from './PlaylistSettings.vue';
import Slideout from './Slideout.vue';


export default {
  name: 'PlaylistDesigner',
  components: {
    MicroCredentialSettings,
    PlaylistPreview,
    PlaylistSettings,
    Slideout,
  },
  data: () => ({
    playlistColor: '#2196F3',
    unusedAttachments: [],
    unusedBulbs: [],
    unusedItems: [],
  }),
  computed: {
    ...mapState([
      'activePlaylistVideo',
      'moduleSelected',
      'passbackBulbs',
      'playlist',
      'playlistId',
      'isUpdating',
      'sectionId',
      'settingsKey',
      'showSlideout',
      'usedAttachmentIds',
      'usedBulbIds',
      'user',
    ]),
    ...mapGetters([
      'restrictPlaylistAccess',
    ]),
    ...mapFields({
      playlistName: 'playlist.name',
    }),
    canEditPlaylist() {
      return [1, 2, 3].includes(this.user.teacherContainerRole);
    },
    dragOptions() {
      return {
        animation: 0,
        group: 'module',
        disabled: false,
        ghostClass: 'ghost',
      };
    },
    isSchoolUser() {
      return _.get(this.user, 'schoolId', false);
    },
    hasMicroCredentialSetup() {
      return (
        (
          _.get(this.user, 'school.badgr.enabled', false)
          && _.get(this.user, 'school.badgr.access_token', false)
        )
        || _.find(this.user?.school?.credentials, { type: 'credly' })
      );
    },
    playlistNameErrors() {
      const errors = [];
      if (!this.$v.playlistName.$dirty) return errors;
      if (!this.$v.playlistName.required) {
        errors.push('Playlist name is required.');
      }
      if (!this.$v.playlistName.maxLength) {
        errors.push('Playlist name max length is 255 characters.');
      }
      return errors;
    },
    selectedModule() {
      return _.find(this.playlist.modules, { id: _.get(this.moduleSelected, 'id', 0) });
    },
  },
  methods: {
    ...mapMutations([
      'pushUsedBulbId',
      'setUsedItemsInPlaylist',
      'toggleSnackbar',
    ]),
    ...mapActions([
      'updatePlaylist',
    ]),
    async bulbPickerMessageReceived(e) {
      if (_.get(e, 'data')) {
        try {
          const pickedBulb = JSON.parse(e.data);
          if (pickedBulb.type === 'playposit__passback_link') {
            if (
              this.usedBulbIds.includes(pickedBulb.bulb_id) === false
              && !_.find(this.passbackBulbs, { bulb_id: pickedBulb.bulb_id })
            ) {
              // Add order in module
              const order = (
                // Newly created modules don't have moduleBulbs yet.
                !_.has(this.selectedModule, 'moduleItems') ? this.selectedModule.moduleBulbs.length : this.selectedModule.moduleItems.length
                + this.passbackBulbs.length
              );
              this.$set(pickedBulb, 'order', order);
              this.$set(this.passbackBulbs, this.passbackBulbs.length, pickedBulb);
              this.pushUsedBulbId(pickedBulb.bulb_id);
              this.toggleSnackbar({
                text: 'Bulb queued!',
                color: 'success',
              });
            } else {
              this.toggleSnackbar({
                text: 'Bulb already in playlist!',
                color: 'warning',
              });
            }
          }
        } catch(err) {
          /*
            There are numerous postMessages that get sent from various extensions,
            webpages, etc.  Since we are parsing the data, and data is a fairly common
            key name - this prevents console errors from trying to parse other messages received.
          */
        }
      }
    },
    setupBulbPickerEventListener() {
      // Listen for bulbPicker post message
      window.addEventListener('message', this.bulbPickerMessageReceived);
    },
    updatePlaylistName(e) {
      if (!this.$v.playlistName.$error && this.$v.playlistName.$dirty) {
        this.$v.playlistName.$reset();
        this.updatePlaylist({
          name: e.target.value,
        });
      }
    },
  },
  mounted() {
    this.playlistColor = _.get(this, 'playlist.default_color', '#2196F3');
    this.setupBulbPickerEventListener();
  },
  created() {
    this.setUsedItemsInPlaylist();
    // Filter out bulbs that are currently being used
    this.unusedBulbs = _.filter(this.playlist.bulbsInSection, bulb => !this.usedBulbIds.includes(bulb.id));
    // Structures the bulb into the correct format
    this.unusedBulbs = this.unusedBulbs.map((bulb) => {
      const moduleBulb = {
        created: bulb.created,
        type: 'bulb',
        bulb,
      };
      return moduleBulb;
    });
    // Filter out attachments that are currently being used
    this.unusedAttachments = _.filter(this.playlist.attachments, attachment => !this.usedAttachmentIds.includes(attachment.attachmentId));
    // Structures the bulb into the correct format
    this.unusedAttachments = this.unusedAttachments.map((attachment) => {
      const moduleAttachment = {
        created: attachment.created,
        type: 'attachment',
        resource: attachment,
      };
      return moduleAttachment;
    });
  },
  validations: {
    playlistName: {
      maxLength: maxLength(255),
      required,
    },
  },
};
</script>

<style lang="scss">
  /* Due to PlaylistPreview being fixed - on small screens we add a large margin to keep column from overlapping */
  .marginLeft {
    margin-left: 50px;
  }
</style>
